import { HttpClient } from '@angular/common/http';
import type { Signal } from '@angular/core';
import { inject, isDevMode, Service, signal, untracked } from '@angular/core';
import type { SafeHtml } from '@angular/platform-browser';
import { DomSanitizer } from '@angular/platform-browser';

import type { IconName } from './icons';

/** Where the SVG files are served from — `public/icons` is copied to the root of the build output. */
const ICON_BASE_PATH = '/icons';

/**
 * Loads the SVG files behind `<app-icon>` and keeps them in memory.
 *
 * Every icon is fetched at most once per app, no matter how many `<app-icon name="check">` elements
 * are on the page, and the response is replayed to all of them through a signal. During SSR the
 * fetch is served from the transfer cache, so the prerendered markup already contains the artwork
 * and the browser does not request it again.
 */
@Service()
export class IconRegistry {
  private readonly http = inject(HttpClient);
  private readonly sanitizer = inject(DomSanitizer);

  private readonly icons = new Map<IconName, Signal<SafeHtml | null>>();

  /** Markup for `name`, or `null` until it has loaded (or if the file is missing). */
  content(name: IconName): Signal<SafeHtml | null> {
    const cached = this.icons.get(name);
    if (cached) {
      return cached;
    }

    // `<app-icon>` calls this from a `computed`, so the fetch must start outside that reactive
    // context: a transfer-cache hit replays the response synchronously, and writing to a signal
    // while a computed is the active consumer throws NG0600.
    return untracked(() => this.load(name));
  }

  private load(name: IconName): Signal<SafeHtml | null> {
    const content = signal<SafeHtml | null>(null);
    this.icons.set(name, content);

    this.http.get(`${ICON_BASE_PATH}/${name}.svg`, { responseType: 'text' }).subscribe({
      // The files are our own build assets, so their markup is trusted; Angular's HTML sanitizer
      // would otherwise strip the whole `<svg>` element.
      next: (svg) => content.set(this.sanitizer.bypassSecurityTrustHtml(svg)),
      error: () => {
        if (isDevMode()) {
          console.warn(`[app-icon] Missing icon file: ${ICON_BASE_PATH}/${name}.svg`);
        }
      },
    });

    return content;
  }
}
